import { useEffect, type RefObject } from "react";
import { navigate } from "vike/client/router";
import { CONTACT_SECTION_ID, PAGE_NAV_ITEMS } from "./Header.config";

const INTERESTED_SECTION_IDS = ["interested", "interested-mobile"] as const;

export const normalizePath = (path: string) =>
  path.endsWith("/") && path !== "/" ? path.slice(0, -1) : path;

export const getActiveNavPath = (
  currentPathname: string,
  isOnHomePage: boolean,
  isContactActive: boolean,
): string | null => {
  if (isOnHomePage) {
    return isContactActive ? null : "/";
  }

  const matched = PAGE_NAV_ITEMS.find((item) => {
    const normalizedPath = normalizePath(item.path);
    return currentPathname === normalizedPath || currentPathname.startsWith(`${normalizedPath}/`);
  });

  return matched?.path ?? null;
};

export const navigateToPath = (path: string) => {
  void navigate(path);
};

const scrollToElementById = (id: string) => {
  const element = document.getElementById(id);
  if (!element) {
    return false;
  }

  element.scrollIntoView({ behavior: "smooth" });
  return true;
};

export const scrollToInterestedSection = () => {
  return INTERESTED_SECTION_IDS.some((sectionId) => scrollToElementById(sectionId));
};

export const scrollToContactSection = () => {
  return scrollToElementById(CONTACT_SECTION_ID);
};

export const useTrackActiveContactSection = (
  isOnHomePage: boolean,
  setActiveSection: (section: string) => void,
) => {
  useEffect(() => {
    if (!isOnHomePage) {
      setActiveSection("");
      return;
    }

    const handleScroll = () => {
      const contactSection = document.getElementById(CONTACT_SECTION_ID);
      const threshold = window.innerHeight * 0.5;
      if (!contactSection) {
        setActiveSection("");
        return;
      }

      const rect = contactSection.getBoundingClientRect();
      if (rect.top <= threshold && rect.bottom > 0) {
        setActiveSection(CONTACT_SECTION_ID);
        return;
      }

      setActiveSection("");
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, [isOnHomePage, setActiveSection]);
};

export const useCloseMenuOnOutsideClick = (
  isMenuOpen: boolean,
  navigationBarRef: RefObject<HTMLDivElement | null>,
  setIsMenuOpen: (isOpen: boolean) => void,
) => {
  useEffect(() => {
    if (!isMenuOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (navigationBarRef.current && !navigationBarRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMenuOpen, navigationBarRef, setIsMenuOpen]);
};

export const useLockBodyScrollOnMobileMenu = (isMenuOpen: boolean) => {
  useEffect(() => {
    const isMobile = window.innerWidth < 600;

    if (isMenuOpen && isMobile) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);
};
